
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "@/hooks/use-user";
import { useNavigate } from "react-router-dom";
import { 
  ChevronRight, 
  MessageCircle, 
  Bell, 
  Star, 
  Briefcase, 
  Shield, 
  Instagram, 
  Twitter, 
  Globe,
  ExternalLink
} from "lucide-react";
import UserSkillsModal from "./UserSkillsModal";
import UserPortfolioModal from "./UserPortfolioModal";
import UserChatModal from "./UserChatModal";

interface SocialLink {
  platform: string;
  url: string;
}

interface ProfileSidebarProps {
  profile: {
    id: string;
    username?: string;
    avatar_url?: string;
    primary_skill?: string;
    social_links?: SocialLink[];
  };
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
  isOwnProfile: boolean;
}

const UserProfileSidebar: React.FC<ProfileSidebarProps> = ({
  profile,
  isCollapsed,
  setIsCollapsed,
  isOwnProfile
}) => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const [isSkillsModalOpen, setIsSkillsModalOpen] = useState(false);
  const [isPortfolioModalOpen, setIsPortfolioModalOpen] = useState(false);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);

  useEffect(() => {
    if (isOwnProfile && user) {
      // Check for unread messages
      const checkUnreadMessages = async () => {
        const { data, error } = await supabase
          .from("chat_messages")
          .select("*")
          .eq("recipient_id", user.id)
          .eq("is_read", false);
        
        if (!error && data) {
          setHasNewMessages(data.length > 0);
        }
      };
      
      checkUnreadMessages();
      
      // Subscribe to new messages
      const subscription = supabase
        .channel('chat_messages_channel')
        .on(
          'postgres_changes',
          { 
            event: 'INSERT', 
            schema: 'public', 
            table: 'chat_messages',
            filter: `recipient_id=eq.${user.id}` 
          },
          (payload) => {
            if (payload.new && payload.new.recipient_id === user.id) {
              setHasNewMessages(true);
            }
          }
        )
        .subscribe();
        
      return () => {
        subscription.unsubscribe();
      };
    }
  }, [user, isOwnProfile]);

  const goToProtectionTools = () => {
    window.open("https://glaze.cs.uchicago.edu/downloads.html", "_blank");
  };

  return (
    <>
      <motion.div 
        className={`bg-background border-r h-[calc(100vh-64px)] fixed top-16 left-0 z-10 shadow-md`}
        initial={{ width: isCollapsed ? 20 : 280 }}
        animate={{ width: isCollapsed ? 20 : 280 }}
        transition={{ type: "spring", stiffness: 400, damping: 30 }}
      >
        <div className="h-full flex flex-col">
          <motion.div 
            className="absolute -right-4 top-6 bg-primary rounded-full z-20 cursor-pointer"
            onClick={() => setIsCollapsed(!isCollapsed)}
            initial={{ rotate: isCollapsed ? 180 : 0 }}
            animate={{ rotate: isCollapsed ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <ChevronRight className="h-5 w-5 text-primary-foreground" />
          </motion.div>
          
          <AnimatePresence>
            {!isCollapsed && (
              <motion.div
                className="flex-1 overflow-y-auto p-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex flex-col items-center space-y-4 mb-6">
                  <Avatar className="h-24 w-24">
                    <AvatarImage src={profile.avatar_url || ""} />
                    <AvatarFallback className="text-xl">
                      {profile.username?.charAt(0).toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="text-center">
                    <h3 className="font-bold text-lg">{profile.username}</h3>
                    {profile.primary_skill && (
                      <p className="text-sm text-muted-foreground">{profile.primary_skill}</p>
                    )}
                  </div>
                  
                  {!isOwnProfile && (
                    <Button 
                      className="w-full" 
                      onClick={() => setIsChatModalOpen(true)}
                    >
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Enviar Mensagem
                    </Button>
                  )}
                </div>
                
                {/* Social Links */}
                <div className="mb-6 space-y-2">
                  <h4 className="text-sm font-medium mb-2">Redes Sociais</h4>
                  <div className="space-y-2">
                    {profile.social_links && profile.social_links.map((link, index) => (
                      <a 
                        key={index} 
                        href={link.url}
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="flex items-center text-sm hover:text-primary transition-colors"
                      >
                        {link.platform === "instagram" && <Instagram className="h-4 w-4 mr-2" />}
                        {link.platform === "twitter" && <Twitter className="h-4 w-4 mr-2" />}
                        {link.platform === "website" && <Globe className="h-4 w-4 mr-2" />}
                        <span className="truncate">{link.url}</span>
                        <ExternalLink className="h-3 w-3 ml-1 flex-shrink-0" />
                      </a>
                    ))}
                    
                    {(!profile.social_links || profile.social_links.length === 0) && (
                      <p className="text-xs text-muted-foreground">Nenhuma rede social adicionada</p>
                    )}
                  </div>
                </div>
                
                {/* Buttons */}
                <div className="space-y-2">
                  <Button 
                    variant="outline" 
                    className="w-full justify-start" 
                    onClick={() => setIsSkillsModalOpen(true)}
                  >
                    <Star className="h-4 w-4 mr-2" />
                    Minhas Skills
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={() => setIsPortfolioModalOpen(true)}
                  >
                    <Briefcase className="h-4 w-4 mr-2" />
                    Trabalhos Realizados
                  </Button>
                  
                  {isOwnProfile && (
                    <Button 
                      variant="outline" 
                      className="w-full justify-start relative"
                      onClick={() => {
                        setIsChatModalOpen(true);
                        setHasNewMessages(false);
                      }}
                    >
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Mensagens
                      {hasNewMessages && (
                        <span className="absolute right-2 top-2 h-2 w-2 bg-destructive rounded-full" />
                      )}
                    </Button>
                  )}
                  
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={goToProtectionTools}
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    Ferramenta de Proteção
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
      
      {/* Modals */}
      {isSkillsModalOpen && (
        <UserSkillsModal 
          isOpen={isSkillsModalOpen}
          onClose={() => setIsSkillsModalOpen(false)}
          userId={profile.id}
          canEdit={isOwnProfile}
        />
      )}
      
      {isPortfolioModalOpen && (
        <UserPortfolioModal
          isOpen={isPortfolioModalOpen}
          onClose={() => setIsPortfolioModalOpen(false)}
          userId={profile.id}
          canEdit={isOwnProfile}
        />
      )}
      
      {isChatModalOpen && (
        <UserChatModal
          isOpen={isChatModalOpen}
          onClose={() => setIsChatModalOpen(false)}
          recipientId={isOwnProfile ? null : profile.id}
          isMessagesView={isOwnProfile}
        />
      )}
    </>
  );
};

export default UserProfileSidebar;
